import React, { useState, useEffect } from 'react';
import { Banknote, Coins, Calculator } from 'lucide-react';

export default function BilletajeGrid({ onChange, values }) {
    const billetes = [100000, 50000, 20000, 10000, 5000, 2000];
    const monedas = [1000, 500, 100, 50];

    const [cantidades, setCantidades] = useState({});

    useEffect(() => {
        if (values && values.length > 0) {
            const initialMap = {};
            values.forEach(v => initialMap[v.denominacion] = v.cantidad);
            setCantidades(initialMap);
        }
    }, [values]);

    const handleChange = (denominacion, valorInput) => {
        if (valorInput === '' || (/^\d+$/.test(valorInput) && parseInt(valorInput) >= 0)) {
            const cantidad = valorInput === '' ? '' : parseInt(valorInput);
            const newCantidades = { ...cantidades, [denominacion]: cantidad };
            setCantidades(newCantidades);

            const items = Object.entries(newCantidades)
                .map(([denom, cant]) => ({
                    denominacion: parseFloat(denom),
                    cantidad: cant === '' ? 0 : cant,
                    tipo: parseFloat(denom) >= 2000 ? 'BILLETE' : 'MONEDA'
                }))
                .filter(i => i.cantidad > 0);

            const total = items.reduce((acc, curr) => acc + (curr.denominacion * curr.cantidad), 0);
            onChange(items, total);
        }
    };

    const formatCurrency = (val) => new Intl.NumberFormat('es-PY').format(val);

    const totalGeneral = Object.entries(cantidades).reduce((acc, [denom, cant]) => {
        return acc + (parseFloat(denom) * (cant === '' ? 0 : cant));
    }, 0);

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Billetes Section */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="bg-slate-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                        <span className="text-[11px] font-black text-slate-600 uppercase tracking-widest flex items-center gap-2">
                            <Banknote size={16} className="text-green-600" /> BILLETES
                        </span>
                        <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">EFECTIVO</span>
                    </div>
                    <div className="divide-y divide-gray-50 p-4 space-y-3">
                        {billetes.map(valor => (
                            <DenominacionRow
                                key={valor}
                                valor={valor}
                                cantidad={cantidades[valor] === undefined ? '' : cantidades[valor]}
                                onChange={handleChange}
                                formatCurrency={formatCurrency}
                            />
                        ))}
                    </div>
                </div>

                {/* Monedas Section */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-fit">
                    <div className="bg-slate-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                        <span className="text-[11px] font-black text-slate-600 uppercase tracking-widest flex items-center gap-2">
                            <Coins size={16} className="text-amber-600" /> MONEDAS
                        </span>
                        <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">MENUDO</span>
                    </div>
                    <div className="divide-y divide-gray-50 p-4 space-y-3">
                        {monedas.map(valor => (
                            <DenominacionRow
                                key={valor}
                                valor={valor}
                                cantidad={cantidades[valor] === undefined ? '' : cantidades[valor]}
                                onChange={handleChange}
                                formatCurrency={formatCurrency}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* Total Footer */}
            <div className="bg-slate-800 text-white p-5 rounded-2xl flex justify-between items-center shadow-xl border border-slate-700">
                <div className="flex items-center gap-4">
                    <div className="bg-slate-700 p-3 rounded-xl border border-slate-600">
                        <Calculator className="text-blue-400" size={24} />
                    </div>
                    <div>
                        <span className="text-[10px] text-slate-400 uppercase font-black tracking-[0.2em] leading-none mb-1 block">Total Arqueado Actual</span>
                        <span className="text-xs text-slate-500 font-bold uppercase">Consolidado Nacional</span>
                    </div>
                </div>
                <div className="text-3xl font-black text-blue-400 tracking-tighter">
                    {formatCurrency(totalGeneral)} <span className="text-sm font-bold text-slate-500 tracking-normal">Gs.</span>
                </div>
            </div>
        </div>
    );
}

const DenominacionRow = ({ valor, cantidad, onChange, formatCurrency }) => {
    const subtotal = (cantidad === '' ? 0 : cantidad) * valor;

    return (
        <div className="flex items-center gap-4 p-3 bg-white border border-gray-100 rounded-lg hover:border-blue-200 hover:bg-slate-50 transition-all group">
            <div className="flex-1">
                <div className="text-[9px] font-bold text-gray-400 uppercase leading-none mb-1">Denominación</div>
                <div className="font-bold text-slate-700 text-base">
                    {formatCurrency(valor)} <span className="text-[10px] text-gray-400">Gs.</span>
                </div>
            </div>
            
            <div className="w-24">
                <div className="text-[9px] font-bold text-gray-400 uppercase leading-none mb-1 text-center font-mono">CANTIDAD</div>
                <input
                    type="text"
                    inputMode="numeric"
                    placeholder="0"
                    className="w-full py-2 px-2 text-center text-xl font-black border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-4 focus:ring-blue-50 focus:outline-none transition-all placeholder-gray-300"
                    value={cantidad}
                    onChange={(e) => onChange(valor, e.target.value)}
                    onFocus={(e) => e.target.select()}
                />
            </div>

            <div className="w-32 text-right">
                <div className="text-[9px] font-bold text-gray-400 uppercase leading-none mb-1">SUBTOTAL</div>
                <div className="font-bold text-blue-700 text-sm">
                    {formatCurrency(subtotal)}
                </div>
            </div>
        </div>
    );
};